import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nonprofit-staging-1/artifacts/7cdd5sle_cropped-HPC-LOGO.jpeg";

const quickLinks = [
  { href: '/about', label: 'About Us' },
  { href: '/impact', label: 'Impact Ecosystem' },
  { href: '/programs', label: 'Programs & Impact' },
  { href: '/partnership', label: 'Partnership' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

const initiatives = [
  { href: 'https://www.rahvai.org', label: 'RAHVAI', desc: 'Rehabilitation & Hope for Africa' },
  { href: 'https://www.seeal.africa', label: 'SEEAL Africa', desc: 'Economic Empowerment' },
  { href: 'https://www.behavioralhealthnavigation.org', label: 'BHNA', desc: 'Behavioral Health Navigation' },
];

export const Footer = () => {
  return (
    <footer className="bg-[#2D3E55] text-white py-20" data-testid="main-footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="His Presence Chapel" 
                className="h-14 w-14 rounded-full object-cover"
              />
            </Link>
            <h3 className="font-heading text-xl font-semibold mb-4">His Presence Chapel Inc.</h3>
            <p className="font-body text-white/80 text-sm leading-relaxed">
              A faith-based nonprofit organization committed to compassionate service, 
              community empowerment, and humanitarian impact.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="font-body text-sm text-white/80 hover:text-[#F7B500] transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Initiatives */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Our Initiatives</h4>
            <ul className="space-y-4">
              {initiatives.map((init) => (
                <li key={init.href}>
                  <a 
                    href={init.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-2"
                    data-testid={`footer-initiative-${init.label.toLowerCase()}`}
                  >
                    <ExternalLink size={14} className="mt-1 text-[#F7B500]" />
                    <div>
                      <span className="font-body text-sm font-medium text-white group-hover:text-[#F7B500] transition-colors">
                        {init.label}
                      </span>
                      <p className="font-body text-xs text-white/60">{init.desc}</p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-[#F7B500] flex-shrink-0" />
                <span className="font-body text-sm text-white/80">
                  Cypress, Texas 77433<br />United States
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#F7B500] flex-shrink-0" />
                <a 
                  href="tel:+18328878254" 
                  className="font-body text-sm text-white/80 hover:text-[#F7B500] transition-colors"
                >
                  +1 (832) 887-8254
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#F7B500] flex-shrink-0" />
                <a 
                  href="mailto:info@hispresencechapel.org" 
                  className="font-body text-sm text-white/80 hover:text-[#F7B500] transition-colors"
                >
                  info@hispresencechapel.org
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-sm text-white/60">
              © {new Date().getFullYear()} His Presence Chapel Inc. All rights reserved.
            </p>
            <p className="font-body text-xs text-white/40">
              A 501(c)(3) nonprofit organization
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
